import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-scroll";
import { CiMenuBurger } from "react-icons/ci";
import {
  FaHome,
  FaBullhorn,
  FaShoppingCart,
} from "react-icons/fa";
import { Button } from "@/app/global-components/button";
import { Link as LinkTo, useNavigate } from "react-router-dom";
import { cn, getData } from "@/lib/utils";
import {
  getMyCompany,
  removeFromCart,
  updateAuthUser,
  updateCartItemQuantity,
} from "@/store/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { isUndefined } from "lodash";
import { MdSpaceDashboard } from "react-icons/md";
import { TbGiftFilled, TbPackages } from "react-icons/tb";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG, ROLE } from "@/types";
import { routes } from "@/consts";
import {
  Badge,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { AddCircle, Delete, RemoveCircle } from "@mui/icons-material";
import LanguageSelector from "@/components/language-selector";
import SidebarView from "./sidebar-view";
import Backdrop from "@/components/backdrop";
import { BiInfoCircle } from "react-icons/bi";

interface NavPageProps {
  isForm: number;
  children: ReactNode;
}

const NavPage: React.FC<NavPageProps> = ({ isForm, children }) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const dispatch = useDispatch();
  const [y, setY] = useState<number>(window.scrollY);
  const [sidebarOpen, toggleSidebar] = useState<boolean>(false);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;

  const toggleSideBarMenu = () => {
    toggleSidebar((prev) => !prev);
  };
  const handleCheckout = () => {
    navigate(routes.checkout);
  };

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    const swipeDistance =
      locale === LANG.EN
        ? touchEndX.current - touchStartX.current
        : touchStartX.current - touchEndX.current;

    if (swipeDistance > 150 && touchEndX.current > 50) {
      toggleSideBarMenu();
    }
  };
  const cart = useSelector((state: RootState) => state.auth.cart);

  const [anchor2El, setAnchor2El] = useState<null | HTMLElement>(null);
  const cartOpen = Boolean(anchor2El);
  const handleCartClose = () => {
    setAnchor2El(null);
  };
  const toggleCart = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchor2El(event.currentTarget);
  };
  const accesToken = getData("tokens")?.accessToken;
  const navigate = useNavigate();
  useEffect(() => {
    const handleScroll = () => setY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [y]);
  const handleQuantityChange = (id: string, quantity: number) => {
    if (quantity >= 1) {
      dispatch(updateCartItemQuantity({ id, quantity }));
    }
  };

  const handleRemoveItem = (id: string) => {
    dispatch(removeFromCart(id));
  };
  const navMenu = [
    {
      title: t("general.home"),
      path: routes.home,
      icon: <MdSpaceDashboard />,
      header: t("navigation.Dashboard"),
    },
    {
      title: t("general.LatestOpportunities"),
      path: routes.tenders,
      icon: <TbGiftFilled />,
      header: t("navigation.OpportunitiesList"),
    },
    {
      title: t("about.title"),
      path: routes.aboutUs,
      icon: <TbPackages />,
      header: t("about.title"),
    },
  ];

  return (
    <>
      <SidebarView
        sidebarOpen={sidebarOpen}
        list={navMenu}
        toggleSidebar={toggleSideBarMenu}
      />
      {sidebarOpen && <Backdrop toggleSidebar={toggleSidebar} />}
      <nav
        className={cn(
          `fixed top-0 w-screen z-50 ${
            y > 100 ? "h-16 !bg-white" : "h-20 !bg-transparent"
          } flex items-center justify-center bg-primary-white transition-all duration-300 border-b`,
          locale === LANG.AR ? "right-0" : "left-0"
        )}
      >
        <div className="container flex px-2 justify-between items-center h-full text-black">
          <Link
            className="flex items-center text-textcolor-black cursor-pointer"
            to={routes.home}
            smooth={true}
          >
            <h1 className="ml-4 text-3xl font-extrabold bg-clip-text text-black">
              Med<span className="text-black">e</span>x
            </h1>
          </Link>
          <div className="flex items-center">
            <div className="md:hidden">
              <LanguageSelector show={false} />
              {userRole === ROLE.BUYER && (
                <IconButton onClick={toggleCart}>
                  <Badge badgeContent={cart.length} color="error">
                    <FaShoppingCart size={24} />
                  </Badge>
                </IconButton>
              )}
            </div>
            <Button
              className="outline-none text-black border-0 bg-transparent h-3/5 px-4 md:hidden"
              onClick={() => toggleSidebar(!sidebarOpen)}
            >
              <CiMenuBurger className="text-2xl" />
            </Button>
          </div>
          <ul className="hidden md:flex items-center">
              <>
                <li className="font-semibold text-sm cursor-pointer flex items-center">
                  {isForm === 1 ? (
                    <Link
                      activeClass="active"
                      className={cn(
                        "px-2 py-2 gap-1 text-textcolor-black flex items-center hover:text-purple-700"
                      )}
                      to={routes.home}
                      spy={true}
                      smooth={true}
                      offset={-80}
                    >
                      <span className="hidden md:inline-block ml-2">
                        <FaHome />
                      </span>
                      {t("general.home")}
                    </Link>
                  ) : (
                    <LinkTo
                      className="px-2 py-2 gap-1 flex items-center"
                      to={routes.home}
                    >
                      {" "}
                      <span className="hidden md:inline-block ml-2">
                        <FaHome />
                      </span>
                      {t("general.home")}
                    </LinkTo>
                  )}
                </li>
                <li className="font-semibold text-textcolor-black text-sm cursor-pointer flex items-center">
                  <Link
                    activeClass="active"
                    className={cn(
                      "px-2 py-2 gap-1 flex items-center hover:text-purple-700"
                    )}
                    to="latestOpportunities"
                    spy={true}
                    smooth={true}
                    offset={-60}
                  >
                    <span className="hidden md:inline-block ml-2">
                      <FaBullhorn />
                    </span>
                    {t("general.LatestOpportunities")}
                  </Link>
                </li>
                <li className="font-semibold text-textcolor-black text-sm cursor-pointer flex items-center">
                  <LinkTo
                    className={cn(
                      "py-2 flex gap-1 items-center hover:text-purple-700",
                      {
                        "border-purple-400 border-b-2":
                          `/${location.pathname.split("/")[2]}` ===
                          routes.aboutUs,
                      }
                    )}
                    to={routes.aboutUs}
                  >
                    <span className="hidden md:inline-block ml-2">
                      <BiInfoCircle />
                    </span>
                    {t("about.title")}
                  </LinkTo>
                </li>
              </>
          </ul>
          <ul className="hidden md:flex gap-2 items-center">
            <li className="font-semibold text-sm cursor-pointer">
              {!accesToken ? (
                <LinkTo
                  to={routes.login}
                  state={{ role: ROLE.BUYER }}
                  className="px-4 py-2 hover:text-purple-700 text-textcolor-black flex items-center"
                >
                  {t("authentication.login")}
                </LinkTo>
              ) : (
                <div
                  onClick={() => {
                    dispatch(updateAuthUser(null));
                    dispatch(getMyCompany(null));
                    localStorage.removeItem("tokens");
                    localStorage.removeItem("userInfo");
                    navigate(routes.home);
                  }}
                  className="px-4 py-2  text-textcolor-black flex items-center"
                  style={{ border: "1px solid #000", borderRadius: 8 }}
                >
                  {t("navigation.Logout")}
                </div>
              )}
            </li>
            {!accesToken ? (
              <li className="font-semibold text-sm cursor-pointer flex items-center">
                <LinkTo
                  to={routes.register}
                  className="rounded-lg bg-secondary-brightYellow text-primary-dark px-4 py-2"
                  style={{ background: "#000", color: "#fff" }}
                >
                  {t("actions.startNow")}
                </LinkTo>
              </li>
            ) : (
              ""
            )}
            <li className="px-4 font-semibold text-sm cursor-pointer flex items-center">
              <LanguageSelector />
            </li>
          </ul>
        </div>
      </nav>
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
      <Menu
        anchorEl={anchor2El}
        open={cartOpen}
        onClose={handleCartClose}
        PaperProps={{
          sx: {
            width: 300,
            maxHeight: 400,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            padding: "10px 0",
            fontWeight: "bold",
            backgroundColor: "#f9f9f9",
            borderBottom: "1px solid #eee",
          }}
        >
          {t("navigation.ShoppingCart")}
        </Typography>
        <List>
          {cart.length > 0 ? (
            cart.map((item) => (
              <ListItem
                key={item.productId}
                sx={{
                  borderBottom: "1px solid #eee",
                  padding: "10px",
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                }}
              >
                {/* Item Image */}
                {item.image ? (
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.unitName}
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 1,
                      border: "1px solid #ddd",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 1,
                      border: "1px solid #ddd",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#f0f0f0",
                      color: "#999",
                    }}
                  >
                    {t("images.noImage")}
                  </Box>
                )}

                {/* Item Details */}
                <Box sx={{ flexGrow: 1 }}>
                  {/* <ListItemText
                    primary={`${item.company.CompanyNr}`}
                    sx={{
                      textAlign: "right",
                      direction: "rtl",
                      fontWeight: "bold",
                    }}
                  /> */}
                  <ListItemText
                    primary={`${item.unitName}`}
                    secondary={`${t(
                      "item.fields.price"
                    )}: ${item.unitPrice.toFixed(2)} ${t(
                      "checkout.summary.currency"
                    )}`}
                    sx={{
                      textAlign: "right",
                      direction: "rtl",
                      marginTop: "4px",
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      marginTop: "8px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ textAlign: "right", direction: "rtl" }}
                    >
                      {t("checkout.fields.quantity")}:
                    </Typography>
                    <IconButton
                      color="primary"
                      onClick={() =>
                        handleQuantityChange(item.productId, item.quantity + 1)
                      }
                    >
                      <AddCircle />
                    </IconButton>
                    <Typography variant="body2">{item.quantity}</Typography>
                    <IconButton
                      color="primary"
                      onClick={() =>
                        handleQuantityChange(item.productId, item.quantity - 1)
                      }
                    >
                      <RemoveCircle />
                    </IconButton>
                  </Box>
                </Box>

                {/* Remove Item */}
                <Box>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveItem(item.productId)}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </ListItem>
            ))
          ) : (
            <Typography
              variant="body2"
              style={{
                padding: "10px",
                textAlign: "center",
                color: "#999",
              }}
            >
              {t("checkout.messages.emptyCart")}
            </Typography>
          )}
        </List>
        <Divider />
        <Box sx={{ padding: "10px", textAlign: "center" }}>
          <Button
            variant="outline"
            color="primary"
            onClick={handleCheckout}
            disabled={cart.length === 0}
          >
            {t("navigation.Checkout")}
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default NavPage;
